import { Component, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, finalize, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { SiteService } from '@card-software/nexus-utilities';
import {
  AvailableInventoryRow,
  AvailableInventoryWarehouse,
  getAvailableInventoryWarehouses,
} from '@app/models/client-purchase-order/client-po-available-inventory';
import { BuildPathFromCode } from '@app/models/inventory/location';
import { InventoryLotProxyService } from '@app/proxies/inventory/inventory-lot.proxy.service';

@Component({
  selector: 'app-client-po-available-inventory',
  templateUrl: './client-po-available-inventory.component.html',
})
export class ClientPoAvailableInventoryComponent {
  private readonly inventoryLotService = inject(InventoryLotProxyService);

  readonly itemCode = input.required<string>();
  readonly uomLabel = input<string>('');

  readonly warehouses = signal<AvailableInventoryWarehouse[]>([]);
  readonly rows = signal<AvailableInventoryRow[]>([]);
  readonly isLoading = signal(0);

  readonly warehouseSubtotals = computed(() =>
    this.warehouses().map((warehouse) => {
      const warehouseRows = this.rows().filter((row) => row.warehouse.locationCode === warehouse.locationCode);
      return {
        warehouse,
        lotItemCount: warehouseRows.reduce((sum, row) => sum + row.lotItemCount, 0),
        totalNetQuantity: warehouseRows.reduce((sum, row) => sum + row.totalNetQuantity, 0),
      };
    }),
  );
  readonly totalLotItems = computed(() => this.rows().reduce((sum, row) => sum + row.lotItemCount, 0));
  readonly totalNetQuantity = computed(() => this.rows().reduce((sum, row) => sum + row.totalNetQuantity, 0));

  private readonly cache = new Map<string, AvailableInventoryRow[]>();

  constructor() {
    toObservable(this.itemCode)
      .pipe(
        switchMap((itemCode) => this.loadInventory(itemCode)),
        takeUntilDestroyed(),
      )
      .subscribe((rows) => this.rows.set(rows));
  }

  private loadInventory(itemCode: string): Observable<AvailableInventoryRow[]> {
    // SiteService is not reactive: read it on every load so a site change applies on the next selection.
    const siteId = SiteService.getSelectedSiteId() || '';
    const warehouses = getAvailableInventoryWarehouses(siteId);
    this.warehouses.set(warehouses);

    if (!itemCode || warehouses.length === 0) return of([]);

    const cacheKey = `${siteId}|${itemCode}`;
    const cached = this.cache.get(cacheKey);
    if (cached) return of(cached);

    // The grouped endpoint does not return the location, so each warehouse is queried on its own.
    this.incrementLoading();
    return forkJoin(
      warehouses.map((warehouse) =>
        this.inventoryLotService
          .searchGroupedByManufacturerLot({
            itemCodes: [itemCode],
            locationPaths: [BuildPathFromCode(warehouse.locationCode)],
            excludeZeroBalances: true,
          })
          .pipe(map((response) => (response.data ?? []).map((group) => ({ ...group, warehouse })))),
      ),
    ).pipe(
      map((rowsByWarehouse) => rowsByWarehouse.flat()),
      tap((rows) => this.cache.set(cacheKey, rows)),
      catchError(() => of([])),
      finalize(() => this.decrementLoading()),
    );
  }

  private incrementLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((v) => Math.max(0, v - 1));
  }
}
